import { useTheme } from '@rneui/themed'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { Platform, StatusBar, useColorScheme } from 'react-native'
import { useUserThemeSetting } from './queries/settings'

export interface ThemeContextValue {
  dark: boolean
  setThemeToSystem: () => void
}

export const ThemeChangeContext = createContext({} as ThemeContextValue)

interface Props {
  children: React.ReactNode
}

export function ThemeChangeProvider(props: Props) {
  const systemThemeSetting = useColorScheme() === 'dark'
  const themeSetting = useUserThemeSetting()
  const [dark, setDark] = useState<boolean>(systemThemeSetting)
  const { updateTheme } = useTheme()

  const applyTheme = useCallback(
    (themeId: string | null | undefined) => {
      if (!themeId) return

      switch (themeId) {
        case 'dark':
          setDark(true)
          StatusBar.setBarStyle('light-content', true)
          setStatusBarBackground('#000')
          break
        case 'light':
          setDark(false)
          StatusBar.setBarStyle('dark-content', true)
          setStatusBarBackground('#fff')
          break
        case 'system':
          setDark(systemThemeSetting)
          StatusBar.setBarStyle(
            systemThemeSetting ? 'light-content' : 'dark-content',
            true
          )
          setStatusBarBackground(systemThemeSetting ? '#000' : '#fff')
          break
      }
    },
    [systemThemeSetting]
  )

  // Function to set theme to system - exposed in context
  const setThemeToSystem = useCallback(() => {
    applyTheme('system')
  }, [applyTheme])

  useEffect(() => {
    if (themeSetting.data?.selectedId) {
      applyTheme(themeSetting.data.selectedId)
    } else {
      setThemeToSystem()
    }
    updateTheme({ mode: dark ? 'dark' : 'light' })
  }, [
    dark,
    applyTheme,
    themeSetting.data?.selectedId,
    updateTheme,
    setThemeToSystem,
  ])

  return (
    <ThemeChangeContext.Provider
      value={{
        dark,
        setThemeToSystem,
      }}
    >
      {props.children}
    </ThemeChangeContext.Provider>
  )
}

export function useThemeChange() {
  return useContext<ThemeContextValue>(ThemeChangeContext)
}

function setStatusBarBackground(color: string) {
  if (Platform.OS === 'android') {
    StatusBar.setBackgroundColor(color, true)
  }
}
